﻿import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import { isProfileComplete } from '@/lib/profiles';

const TUTORIAL_KEY = 'tutorial_v1';

// Επιτρέπει σε άλλες οθόνες (π.χ. Ρυθμίσεις) να ξαναδείξουν τον οδηγό.
let replayListener: (() => void) | null = null;
export async function replayTutorial(): Promise<void> {
  try {
    await AsyncStorage.removeItem(TUTORIAL_KEY);
  } catch {
    // αγνόησε
  }
  replayListener?.();
}

// Ειδοποίηση όταν κλείνει ο οδηγός (ώστε π.χ. να ξεκινήσουν τα coachmarks).
let dismissListener: (() => void) | null = null;
export function setTutorialDismissListener(fn: (() => void) | null): void {
  dismissListener = fn;
}

// Ειδοποίηση για το αν το tutorial είναι ορατό αυτή τη στιγμή, ώστε άλλα
// modals (π.χ. OTA restart prompt) να περιμένουν να τελειώσει πρώτα αντί να
// στοιβάζονται από πάνω του.
const activeListeners = new Set<(active: boolean) => void>();
export function subscribeTutorialActive(fn: (active: boolean) => void): () => void {
  activeListeners.add(fn);
  return () => {
    activeListeners.delete(fn);
  };
}
function notifyTutorialActive(active: boolean) {
  activeListeners.forEach((fn) => fn(active));
}

type Slide = {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  text: string;
};

/** Tutorial εκμάθησης που εμφανίζεται μία φορά, μετά την ολοκλήρωση του προφίλ. */
export function Tutorial() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { profile } = useProfile();

  const SLIDES: Slide[] = useMemo(() => [
    { icon: 'document-text-outline', title: t('tutorial.slide1_title'), text: t('tutorial.slide1_text') },
    { icon: 'paper-plane-outline', title: t('tutorial.slide2_title'), text: t('tutorial.slide2_text') },
    { icon: 'checkmark-done-outline', title: t('tutorial.slide3_title'), text: t('tutorial.slide3_text') },
    { icon: 'create-outline', title: t('tutorial.slide4_title'), text: t('tutorial.slide4_text') },
    { icon: 'star-outline', title: t('tutorial.slide5_title'), text: t('tutorial.slide5_text') },
    { icon: 'shield-checkmark-outline', title: t('tutorial.slide6_title'), text: t('tutorial.slide6_text') },
  ], [t]);
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    notifyTutorialActive(visible);
  }, [visible]);

  // Σύνδεση για επανεμφάνιση από τις Ρυθμίσεις.
  useEffect(() => {
    replayListener = () => {
      setIndex(0);
      setVisible(true);
    };
    return () => {
      replayListener = null;
    };
  }, []);

  useEffect(() => {
    // Δείξε μόνο όταν ο χρήστης έχει ολοκληρώσει το προφίλ (μετά το onboarding).
    if (!isProfileComplete(profile)) return;
    let mounted = true;
    void AsyncStorage.getItem(TUTORIAL_KEY).then((v) => {
      if (mounted && !v) setVisible(true);
    });
    return () => {
      mounted = false;
    };
  }, [profile]);

  if (!visible) return null;

  const isLast = index === SLIDES.length - 1;

  async function finish() {
    try {
      await AsyncStorage.setItem(TUTORIAL_KEY, '1');
    } catch {
      // αγνόησε — απλώς κλείσε
    }
    setVisible(false);
    dismissListener?.();
  }

  function next() {
    if (isLast) {
      void finish();
      return;
    }
    setIndex(index + 1);
  }

  const slide = SLIDES[index];

  return (
    <View
      style={[styles.overlay, { paddingTop: insets.top, paddingBottom: insets.bottom }]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => void finish()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.skip}>{t('tutorial.skip')}</Text>
        </Pressable>
      </View>

      <View style={[styles.slide, { width }]}>
        <View style={styles.iconWrap}>
          <Ionicons name={slide.icon} size={64} color={colors.primary} />
        </View>
        <Text style={styles.slideTitle}>{slide.title}</Text>
        <Text style={styles.slideText}>{slide.text}</Text>
      </View>

      <View style={styles.dots}>
        {SLIDES.map((s, i) => (
          <View key={s.title} style={[styles.dot, i === index && styles.dotActive]} />
        ))}
      </View>

      <View style={styles.footer}>
        <Button
          title={isLast ? t('tutorial.start') : t('tutorial.next')}
          icon={isLast ? 'rocket-outline' : 'arrow-forward'}
          onPress={next}
        />
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.background,
      zIndex: 50,
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.sm,
    },
    skip: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    slide: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.xl,
      gap: spacing.lg,
    },
    iconWrap: {
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center',
      justifyContent: 'center',
    },
    slideTitle: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '800',
      textAlign: 'center',
    },
    slideText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 24,
      textAlign: 'center',
    },
    dots: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: spacing.xs,
      paddingVertical: spacing.lg,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.border,
    },
    dotActive: {
      backgroundColor: colors.primary,
      width: 22,
    },
    footer: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.lg,
    },
  });
}
